import { useEffect, useState } from "react";
import { Bell, X, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import {
    fetchNotifications, markAllNotificationsRead, sendThanks,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function NotificationsBell() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);

    const load = async () => {
        try {
            const d = await fetchNotifications();
            setItems(d.notifications || []);
            setUnread(d.unread || 0);
        } catch (err) {
            console.error("NotificationsBell: failed to load", err);
        }
    };

    useEffect(() => {
        if (!user) return;
        load();
        const id = setInterval(load, 45000);
        return () => clearInterval(id);
    }, [user]);

    if (!user) return null;

    const onToggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && unread > 0) {
            await markAllNotificationsRead();
            setUnread(0);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={onToggle}
                data-testid="nav-bell"
                className="relative font-mono text-xs uppercase text-[var(--muted)] hover:text-white p-2"
                aria-label="Notifications"
            >
                <Bell size={16} />
                {unread > 0 && (
                    <span
                        data-testid="nav-bell-badge"
                        className="absolute -top-0.5 -right-0.5 bg-[var(--coral)] text-white text-[9px] font-bold w-4 h-4 grid place-items-center"
                    >
                        {unread}
                    </span>
                )}
            </button>
            {open && (
                <div
                    className="absolute top-full right-0 mt-2 w-[320px] terminal z-50"
                    data-testid="notif-panel"
                >
                    <div className="terminal-header px-3 py-2 flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--lime)]">
                            // notifications
                        </span>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-[var(--muted)] hover:text-white"
                        >
                            <X size={14} />
                        </button>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto">
                        {items.length === 0 ? (
                            <p className="font-mono text-xs text-[var(--muted)] p-4">
                                nothing here yet.
                            </p>
                        ) : (
                            items.map((n) => (
                                <NotifItem
                                    key={n.id}
                                    n={n}
                                    onClose={() => setOpen(false)}
                                />
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function NotifItem({ n, onClose }) {
    const [thanked, setThanked] = useState(!!n.thanked);
    const stamp = new Date(n.created_at).toLocaleString();
    const onThanks = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (thanked) return;
        setThanked(true);
        try {
            await sendThanks(n.id);
        } catch (_) {
            setThanked(false);
        }
    };
    const inner = (
        <div
            className={
                "p-3 border-b border-[var(--border)] " +
                (n.kind === "water_alert"
                    ? "border-l-2 border-l-[var(--coral)] bg-[var(--coral)]/[0.05]"
                    : "hover:bg-[var(--lime)]/[0.04]")
            }
        >
            <div className="font-mono text-xs text-white">{n.title}</div>
            <div className="font-mono text-[10px] text-[var(--muted)] mt-1 flex items-center justify-between gap-2">
                <span>{stamp}</span>
                {n.kind === "cheer" && (
                    <button
                        type="button"
                        onClick={onThanks}
                        disabled={thanked}
                        data-testid={`thanks-${n.id}`}
                        className={
                            "inline-flex items-center gap-1 px-2 py-0.5 border transition-colors " +
                            (thanked
                                ? "border-[var(--lime)] text-[var(--lime)]"
                                : "border-[var(--coral)] text-[var(--coral)] hover:bg-[var(--coral)]/10")
                        }
                    >
                        <Heart
                            size={10}
                            fill={thanked ? "var(--lime)" : "none"}
                        />
                        {thanked ? "thanked" : "thanks!"}
                    </button>
                )}
            </div>
        </div>
    );
    if (n.kind === "lesson_plan_shared") {
        return (
            <Link to="/dashboard" onClick={onClose} data-testid={`notif-${n.id}`}>
                {inner}
            </Link>
        );
    }
    return (
        <div data-testid={`notif-${n.id}`} className="cursor-default">
            {inner}
        </div>
    );
}
